import React, { useState, useEffect } from "react";
import { useAuth } from '../../hooks/useAuth';
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";
import './Login.css';
import ForgotPasswordModal from './ForgotPasswordModal';
import { runSecurityTests } from '../../utils/securityTest';
import { 
  trackLoginAttempt, 
  isAccountLocked, 
  getLockoutTimeRemaining,
  resetLoginAttempts,
  getLoginAttempts
} from '../../utils/loginAttemptTracker';

const BACKEND_PORT = process.env.REACT_APP_API_BASE_URL;
const WARNING_THRESHOLD = 5; // Show warning after 5 attempts

export default function Login() {
    const { setIsAuthenticated, setUser } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [lockoutTimeRemaining, setLockoutTimeRemaining] = useState(0);
    const [remainingAttempts, setRemainingAttempts] = useState(10);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    // Check for lockout status and remaining attempts when email changes
    useEffect(() => {
      if (email) {
        const timeRemaining = getLockoutTimeRemaining(email);
        setLockoutTimeRemaining(timeRemaining);
        
        const attempts = getLoginAttempts(email);
        setRemainingAttempts(10 - attempts.count);
      }
    }, [email]);

    const fetchUserProfile = async (token) => {
        try {
            const userProfileResponse = await axiosInstance.get('/users/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setUser(userProfileResponse.data);
        } catch (error) {
            console.error('Failed to fetch user profile:', error);
        }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoginError("");

      // Trim inputs
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();

      // Check if account is locked
      if (isAccountLocked(trimmedEmail)) {
        const timeRemaining = getLockoutTimeRemaining(trimmedEmail);
        setLoginError(`Account temporarily locked. Please try again in ${timeRemaining} minutes.`);
        return;
      }

      // Run security tests
      const securityResults = runSecurityTests(trimmedEmail + trimmedPassword);
      if (Object.values(securityResults).some(result => result)) {
        setLoginError("Invalid input detected");
        return;
      }

      // Basic validation
      if (!trimmedEmail || !trimmedPassword) {
        setLoginError("Please fill in all fields");
        return;
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        setLoginError("Please enter a valid email address");
        return;
      }

      try {
        const response = await axiosInstance.post('/users/login', {
          email: trimmedEmail,
          password: trimmedPassword
        });
        if (response.status === 200) {
          const data = response.data;
          localStorage.setItem('token', data.token);
          setIsAuthenticated(true);
          await fetchUserProfile(data.token);
          resetLoginAttempts(trimmedEmail); // Reset attempts on successful login
          setRemainingAttempts(10); // Reset remaining attempts display

          // Redirect the user back to the page they came from, or /Profile if none provided
          const from = location.state?.from || '/Profile';
          navigate(from, { replace: true });
        } else {
          setLoginError("Login failed. Please check your credentials.");
        }
      } catch (err) {
        // Track failed attempt
        const attempts = trackLoginAttempt(trimmedEmail);
        setRemainingAttempts(10 - attempts.count);
        
        if (err.response) {
          setLoginError(err.response.data.message);
        } else {
          setLoginError("An error occurred. Please try again.");
        }

        // Check if account is now locked
        if (isAccountLocked(trimmedEmail)) {
          const timeRemaining = getLockoutTimeRemaining(trimmedEmail);
          setLoginError(`Account temporarily locked. Please try again in ${timeRemaining} minutes.`);
        }
      }
    };

    const handleOpenForgotPassword = () => {
      setShowForgotPassword(true);
    };

    const handleCloseForgotPassword = () => {
      setShowForgotPassword(false);
    };

    const renderAttemptsWarning = () => {
      if (remainingAttempts <= WARNING_THRESHOLD && remainingAttempts > 0) {
        return (
          <div className="attempts-warning">
            {remainingAttempts} {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining before account is locked for 15 minutes
          </div>
        );
      }
      return null;
    };

    return (
      <>
      <div className="login-container">
        <div className="login-box">
          <h1 className="login-title">Login</h1>
          <form onSubmit={handleSubmit} className="login-form">
            <input 
              type="text" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="Email" 
              className="login-input"
              disabled={lockoutTimeRemaining > 0}
            />
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              placeholder="Password" 
              className="login-input"
              disabled={lockoutTimeRemaining > 0}
            />
            <button 
              type="submit" 
              className="new-account-button"
              disabled={lockoutTimeRemaining > 0}
            >
              {lockoutTimeRemaining > 0 ? `Try again in ${lockoutTimeRemaining} minutes` : 'Login'}
            </button>
          </form>
          <button 
            type="button" 
            className="forgotPassword-button" 
            onClick={handleOpenForgotPassword}
            style={{ marginBottom: "1em" }}
          >
            Forgot Password?
          </button>
          {loginError && <div className="login-error">{loginError}</div>}
          {renderAttemptsWarning()}
        </div>
        {showForgotPassword && <ForgotPasswordModal onClose={handleCloseForgotPassword} />}
      </div>
      <div style={{ 
        aspectRatio: "960/110", 
        backgroundImage: "url('/greyToPinkLogin.png')",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center"
    }}></div>
  </>
    );
};
